// Utility for generating placeholder images with improved IPFS handling

import { ipfsGateways } from "../config"


export const PLACEHOLDER_IMAGE_DATA_URI = `data:image/svg+xml;base64,${btoa(`
<svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
  <rect width="100%" height="100%" fill="#374151"/>
  <text x="50%" y="45%" text-anchor="middle" fill="#9CA3AF" font-family="Arial, sans-serif" font-size="16">
    No Image
  </text>
  <text x="50%" y="55%" text-anchor="middle" fill="#9CA3AF" font-family="Arial, sans-serif" font-size="16">
    Available
  </text>
</svg>
`)}`

/**
 * Test if a URL is likely an IPFS URL
 */
function isIpfsUrl(url: string): boolean {
  if (!url) return false

  return url.startsWith('ipfs://') ||
         url.startsWith('ipfs/') ||
         url.includes('/ipfs/') ||
         url.match(/^Qm[1-9A-HJ-NP-Za-km-z]{44}$/) !== null ||
         url.match(/^[a-z2-7]{59}$/) !== null
}

/**
 * Convert various IPFS URL formats to HTTP URL using configured gateways
 */
function convertIpfsUrl(url: string, gatewayIndex = 0): string {
  if (!url) return url

  // Already a standard HTTP URL (not IPFS)
  if (url.startsWith('http') && !url.includes('/ipfs/')) {
    return url
  }

  let ipfsPath = ''

  // Extract IPFS hash and path from various formats
  if (url.startsWith('ipfs://')) {
    ipfsPath = url.replace('ipfs://', '')
  } else if (url.startsWith('ipfs/')) {
    ipfsPath = url.replace('ipfs/', '')
  } else if (url.includes('/ipfs/')) {
    // Extract hash and path from existing gateway URL
    const ipfsIndex = url.indexOf('/ipfs/')
    ipfsPath = url.substring(ipfsIndex + 6) // +6 for '/ipfs/'
  } else if (url.match(/^Qm[1-9A-HJ-NP-Za-km-z]{44}$/)) {
    // Raw IPFS hash (Qm format) - no additional path
    ipfsPath = url
  } else if (url.match(/^[a-z2-7]{59}$/)) {
    // Raw IPFS hash (newer CID format) - no additional path
    ipfsPath = url
  } else {
    // Not an IPFS URL, return as is
    return url
  }

  // Extract just the hash part for validation, but preserve the full path
  const hashPart = ipfsPath.split('/')[0]

  // Validate hash format
  if (!hashPart || (!hashPart.startsWith('Qm') && !hashPart.match(/^[a-z2-7]{59}$/) && !hashPart.startsWith('bafy'))) {
    return url // Return original if can't parse
  }

  const gateway = ipfsGateways[gatewayIndex] || ipfsGateways[0]
  console.log(`🔄 Converting IPFS URL: ${url} → ${gateway}${ipfsPath}`)
  return `${gateway}${ipfsPath}` // Preserve full path including token ID and extension
}

/**
 * Get image URL with IPFS conversion and fallback to placeholder
 */
export const getImageWithFallback = (imageUrl?: string | null): string => {
  if (!imageUrl) return PLACEHOLDER_IMAGE_DATA_URI

  // Handle IPFS URLs by converting to HTTP using first gateway
  if (isIpfsUrl(imageUrl)) {
    try {
      return convertIpfsUrl(imageUrl, 0) // Use first gateway by default
    } catch (error) {
      console.warn('⚠️ Failed to convert IPFS URL:', error)
      return PLACEHOLDER_IMAGE_DATA_URI
    }
  }

  return imageUrl
}

/**
 * Get image URL with multiple IPFS gateway fallbacks (for critical image loading)
 */
export const getImageWithMultipleGateways = (imageUrl?: string | null): string[] => {
  if (!imageUrl) return [PLACEHOLDER_IMAGE_DATA_URI]

  if (isIpfsUrl(imageUrl)) {
    try {
      // Return all possible gateway URLs for the image
      const urls = ipfsGateways.map((_, index) => convertIpfsUrl(imageUrl, index))
      urls.push(PLACEHOLDER_IMAGE_DATA_URI) // Add fallback at the end
      return urls
    } catch (error) {
      console.warn('⚠️ Failed to convert IPFS URL for multiple gateways:', error)
      return [PLACEHOLDER_IMAGE_DATA_URI]
    }
  }

  return [imageUrl, PLACEHOLDER_IMAGE_DATA_URI]
}